import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { BuildingsIcon } from "@phosphor-icons/react/dist/ssr";

import { normalizePhotoUrl } from "@/utilities/helpers/normalizePhotoUrl";

import { pickPriceField } from "./EntryCard";

const NO_IMAGE = "https://media.grupoguia.mx/assets/home-page/fichas/nodisponible.webp";

const formatMXN = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    return `$${n.toLocaleString("es-MX")} MXN`;
};

const formatPropertyAddress = (locationProps) => {
    if (!locationProps) return "";
    const parts = [
        locationProps.street_name,
        locationProps.exterior_number,
        locationProps.city && `, ${locationProps.city}`,
        locationProps.province && `, ${locationProps.province}`,
    ].filter(Boolean);
    return parts.join(" ").replace(/\s+,/g, ",");
};

const formatDevLocation = (dev) =>
    dev.location_props?.name ||
    [dev.location_props?.city, dev.location_props?.province].filter(Boolean).join(", ");

const buildPropertyHref = (item) => {
    // `wiggot_code` is the short Wiggot id (e.g. "pZdVST9") that the detail
    // page expects — its `/property/<id>` backend call resolves a row only
    // for the short id, not for our internal `property_id` UUID. Using
    // `property_id` here was the bug behind reports of "Lo sentimos, esta
    // propiedad no está disponible" pages after marker clicks.
    const id = item.wiggot_code;
    if (!id) return null;
    const op = item.presale ? "presale" : item.sale ? "sale" : "lease";
    return `/Propiedad/wig-${id}?operation=${op}`;
};

const buildDevHref = (dev) => (dev.url_name ? `/desarrollo/${dev.url_name}` : null);

// Compact marker-click preview card. Sized to fit Google Maps' InfoWindow
// without forcing an auto-pan (~300×320). Reuses neither FeaturedPropCard
// nor DevsCard — both are 450px tall + designed for grid context, and
// shrinking them via props turned out to be a heavier lift than just
// drawing a slim version. Whole card is the click target — opens the
// listing detail in a new tab, matching the `isForMap` behavior of the
// existing search cards.
//
// No X close button (Zillow doesn't have one either). Close paths:
// click off-map, click another marker, Esc — all wired in MapPane.
const MarkerPreviewCard = ({ entry, state }) => {
    if (!entry) return null;
    const isProperty = entry.kind === "property";
    const item = entry.data;

    const href = isProperty ? buildPropertyHref(item) : buildDevHref(item);
    const imageUrl = normalizePhotoUrl(item.photo) || NO_IMAGE;

    const title = isProperty ? item.title : item.name;
    const address = isProperty
        ? formatPropertyAddress(item.location_props)
        : formatDevLocation(item);

    const priceRaw = isProperty
        ? pickPriceField(item, state?.operation)
        : item.min_presale && item.min_presale !== "1"
          ? item.min_presale
          : item.min_price && item.min_price !== "1"
            ? item.min_price
            : item.min_sale || item.min_lease;
    const price = formatMXN(priceRaw);
    const pricePrefix = isProperty ? "" : "Desde ";

    // Property stats: beds · baths · m². Skip a token when its value is
    // missing — empty middle dots read worse than a shorter line.
    const propStats = [];
    if (isProperty) {
        const bd = item.structure_props?.bedrooms;
        const ba = item.structure_props?.bathrooms;
        const sz = item.structure_props?.construction_size || item.structure_props?.ground_size;
        if (bd) propStats.push(`${bd} rec`);
        if (ba) propStats.push(`${ba} baños`);
        if (sz) propStats.push(`${sz} m²`);
    }
    const devBedRange = !isProperty
        ? (() => {
              const min = item.product_props?.min_bedrooms;
              const max = item.product_props?.max_bedrooms;
              if (min && max && min !== max) return `${min}–${max} rec`;
              if (min) return `${min} rec`;
              return null;
          })()
        : null;
    const devSizeRange = !isProperty
        ? (() => {
              const minC = item.product_props?.min_construction_size;
              const maxC = item.product_props?.max_construction_size;
              if (minC && maxC && minC !== maxC) return `${minC}–${maxC} m²`;
              if (minC) return `${minC} m²`;
              const minG = item.product_props?.min_ground_size;
              const maxG = item.product_props?.max_ground_size;
              if (minG && maxG && minG !== maxG) return `${minG}–${maxG} m²`;
              if (minG) return `${minG} m²`;
              return null;
          })()
        : null;
    const stats = isProperty
        ? propStats.join(" · ")
        : [devBedRange, devSizeRange].filter(Boolean).join(" · ");

    const Body = (
        <Box
            w="300px"
            bg="white"
            rounded="lg"
            overflow="hidden"
            cursor={href ? "pointer" : "default"}
            transition="box-shadow 0.15s"
            _hover={href ? { boxShadow: "md" } : undefined}
        >
            <Box position="relative">
                <Image
                    src={imageUrl}
                    alt={title || ""}
                    w="full"
                    h="160px"
                    objectFit="cover"
                    loading="lazy"
                    decoding="async"
                />
                {!isProperty && (
                    <Flex
                        position="absolute"
                        top={2}
                        right={2}
                        bg="brand.500"
                        color="accent.500"
                        w="28px"
                        h="28px"
                        rounded="full"
                        align="center"
                        justify="center"
                        boxShadow="sm"
                    >
                        <BuildingsIcon weight="fill" size={16} />
                    </Flex>
                )}
            </Box>
            <Box p={3}>
                {price && (
                    <Text fontSize="lg" fontWeight={700} color="accent.500">
                        {pricePrefix}
                        {price}
                    </Text>
                )}
                {stats && (
                    <Text fontSize="sm" color="gray.700" mt={0.5}>
                        {stats}
                    </Text>
                )}
                {title && (
                    <Text fontSize="sm" fontWeight={600} mt={1.5} lineClamp={1}>
                        {title}
                    </Text>
                )}
                {address && (
                    <Text fontSize="xs" color="gray.500" lineClamp={2}>
                        {address}
                    </Text>
                )}
            </Box>
        </Box>
    );

    if (!href) return Body;
    // New tab matches `isForMap` behavior on the existing search cards —
    // the user keeps their map context open behind the detail page.
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", color: "inherit" }}
        >
            {Body}
        </a>
    );
};

export default MarkerPreviewCard;
